import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { useAuth } from "@/AuthContext";
import { Plus, Copy, LogOut, MessageSquareQuote, ChevronRight, Mail, Send, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { LangSwitcher } from "@/components/LangSwitcher";

export default function Dashboard() {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState(null);
  const [usage, setUsage] = useState(null);
  const [digestEnabled, setDigestEnabled] = useState(true);
  const [sendingPreview, setSendingPreview] = useState(false);

  useEffect(() => {
    api.get("/campaigns").then((r) => setCampaigns(r.data)).catch(() => setCampaigns([]));
    api.get("/usage").then((r) => setUsage(r.data)).catch(() => {});
    api.get("/auth/me").then((r) => setDigestEnabled(r.data.weekly_digest_enabled !== false)).catch(() => {});
  }, []);

  const toggleDigest = async (v) => {
    setDigestEnabled(v);
    try {
      await api.patch("/user/preferences/weekly-digest", { enabled: v });
      toast.success(v ? t("dashboard.digestOn") : t("dashboard.digestOff"));
    } catch {
      setDigestEnabled(!v);
      toast.error(t("campaignForm.saveError"));
    }
  };

  const sendPreview = async () => {
    setSendingPreview(true);
    try {
      const r = await api.post("/user/weekly-digest/preview");
      toast.success(t("dashboard.digestSent", { email: r.data.sent_to }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("dashboard.digestFail"));
    } finally {
      setSendingPreview(false);
    }
  };

  const copyLink = (slug) => {
    navigator.clipboard.writeText(`${window.location.origin}/c/${slug}`);
    toast.success(t("dashboard.linkCopied"));
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <header className="border-b border-zinc-200 bg-white">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="font-heading font-extrabold text-xl tracking-tight">depoia<span className="text-[#FF4F00]">.</span></Link>
          <div className="flex items-center gap-4">
            <LangSwitcher />
            <div className="flex items-center gap-2">
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-8 h-8 rounded-full" referrerPolicy="no-referrer" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-[#FF4F00] text-white flex items-center justify-center text-sm font-bold">
                  {user?.name?.[0]}
                </div>
              )}
              <span className="text-sm font-medium hidden sm:block" data-testid="user-name">{user?.name}</span>
            </div>
            <button data-testid="logout-btn" onClick={handleLogout} className="text-zinc-400 hover:text-zinc-700 transition-colors" title="Sair">
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10">
        {usage && usage.plan === "free" && (
          <div data-testid="plan-usage-banner" className="mb-8 bg-white border border-zinc-200 rounded-lg p-5 flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-sm font-semibold">
                {t("common.freePlan")}
                <span className="text-zinc-500 font-medium ml-2" data-testid="usage-counts">
                  {usage.media_used}/{usage.media_limit} {t("record.modeVideo").toLowerCase()}/{t("record.modeAudio").toLowerCase()} · {usage.text_used}/{usage.text_limit} {t("record.modeText").toLowerCase() === "write" ? "texts" : "textos"}
                </span>
              </p>
              <p className="text-xs text-zinc-500 mt-1">{t("dashboard.freeHint")}</p>
            </div>
            <div className="flex items-center gap-3">
              <Link data-testid="billing-link-free" to="/billing" className="text-sm font-semibold text-zinc-600 underline underline-offset-4 hover:text-[#FF4F00] transition-colors">
                {t("common.billing")}
              </Link>
              <Link data-testid="upgrade-btn" to="/pricing" className="px-5 py-2.5 bg-[#FF4F00] text-white rounded-full text-sm font-semibold hover:bg-[#E64600] transition-colors">
                {t("common.upgrade")}
              </Link>
            </div>
          </div>
        )}
        {usage && usage.plan !== "free" && (
          <div className="mb-8 flex flex-wrap items-center gap-3">
            <div data-testid="plan-pro-badge" className="inline-flex items-center gap-2 px-4 py-2 bg-[#0A0A0A] text-white rounded-full text-xs font-bold uppercase tracking-wider">
              {t("common.paidPlan", { plan: usage.plan })}
            </div>
            <Link data-testid="billing-link" to="/billing" className="text-sm font-semibold text-zinc-600 underline underline-offset-4 hover:text-[#FF4F00] transition-colors">
              {t("common.billing")}
            </Link>
          </div>
        )}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading font-extrabold text-3xl tracking-tight">{t("dashboard.campaigns")}</h1>
            <p className="text-sm text-zinc-500 mt-1">{t("dashboard.campaignsSub")}</p>
          </div>
          <div className="flex items-center gap-2">
            {user?.is_admin && (
              <Link
                data-testid="admin-panel-link"
                to="/admin"
                title={t("dashboard.adminPanelTitle")}
                className="px-4 py-3 bg-zinc-900 text-white rounded-full text-sm font-semibold flex items-center gap-2 hover:bg-zinc-700 transition-colors"
              >
                <Sparkles size={14} />
                <span className="hidden sm:inline">{t("dashboard.adminPanel")}</span>
              </Link>
            )}
            <Link
              data-testid="new-campaign-btn"
              to="/campaigns/new"
              className="px-5 py-3 bg-[#FF4F00] text-white rounded-full text-sm font-semibold flex items-center gap-2 hover:bg-[#E64600] transition-colors"
            >
              <Plus size={16} /> {t("dashboard.newCampaign")}
            </Link>
          </div>
        </div>

        {campaigns === null ? (
          <div className="text-sm text-zinc-400 py-20 text-center">{t("common.loading")}</div>
        ) : campaigns.length === 0 ? (
          <div data-testid="empty-campaigns" className="border border-dashed border-zinc-300 rounded-lg py-20 flex flex-col items-center gap-4">
            <MessageSquareQuote size={36} className="text-zinc-300" />
            <p className="text-zinc-500 text-sm">{t("dashboard.emptyMsg")}</p>
            <Link data-testid="empty-new-campaign-btn" to="/campaigns/new" className="px-6 py-3 bg-[#0A0A0A] text-white rounded-full text-sm font-semibold hover:bg-zinc-800 transition-colors">
              {t("dashboard.emptyBtn")}
            </Link>
          </div>
        ) : (
          <div className="bg-white border border-zinc-200 rounded-lg divide-y divide-zinc-100">
            {campaigns.map((c) => (
              <div key={c.id} data-testid={`campaign-row-${c.id}`} className="p-5 flex items-center gap-4 hover:bg-zinc-50 transition-colors">
                <Link to={`/campaigns/${c.id}`} className="flex-1 min-w-0" data-testid={`campaign-link-${c.id}`}>
                  <p className="font-semibold truncate flex items-center gap-2">
                    {c.business_name}
                    {c.new_count > 0 && (
                      <span data-testid={`new-badge-${c.id}`} className="px-2 py-0.5 bg-[#FF4F00] text-white text-[10px] font-bold rounded-full shrink-0">
                        {c.new_count} {c.new_count > 1 ? (t("common.langLabel") === "Language" ? "new" : "novos") : (t("common.langLabel") === "Language" ? "new" : "novo")}
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-zinc-500 mt-0.5">
                    {c.testimonial_count} {t("dashboard.testimonials")} · {c.approved_count} {t("dashboard.wallCount")}
                  </p>
                </Link>
                <button
                  data-testid={`copy-link-btn-${c.id}`}
                  onClick={() => copyLink(c.slug)}
                  className="px-4 py-2 border border-zinc-200 rounded-full text-xs font-semibold flex items-center gap-1.5 text-zinc-600 hover:border-[#FF4F00] hover:text-[#FF4F00] transition-colors"
                >
                  <Copy size={13} /> {t("common.copyLink")}
                </button>
                <a
                  data-testid={`whatsapp-share-btn-${c.id}`}
                  href={`https://wa.me/?text=${encodeURIComponent(`${(c.whatsapp_message || "Oi! Adorei ter trabalhado com você. Pode gravar um depoimento rapidinho? Leva 1 minuto, é só abrir esse link:").trim()} ${window.location.origin}/c/${c.slug}`)}`}
                  target="_blank"
                  rel="noreferrer"
                  title="Compartilhar no WhatsApp"
                  className="w-8 h-8 rounded-full bg-[#25D366] text-white flex items-center justify-center hover:bg-[#1EBE5A] transition-colors shrink-0"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.297-.497.1-.198.05-.371-.025-.52-.074-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                </a>
                <Link to={`/campaigns/${c.id}`} className="text-zinc-300 hover:text-zinc-600 transition-colors">
                  <ChevronRight size={18} />
                </Link>
              </div>
            ))}
          </div>
        )}

        <div data-testid="digest-pref-card" className="mt-10 bg-white border border-zinc-200 rounded-lg p-5 flex items-center gap-4 flex-wrap">
          <div className="w-10 h-10 rounded-full bg-orange-50 border border-orange-100 flex items-center justify-center shrink-0">
            <Mail size={18} className="text-[#FF4F00]" />
          </div>
          <div className="flex-1 min-w-[220px]">
            <p className="font-semibold text-sm">{t("dashboard.digestTitle")}</p>
            <p className="text-xs text-zinc-500 mt-0.5">{t("dashboard.digestDesc")}</p>
          </div>
          <button
            data-testid="digest-preview-btn"
            onClick={sendPreview}
            disabled={sendingPreview}
            className="px-4 py-2 border border-zinc-200 rounded-full text-xs font-semibold flex items-center gap-1.5 text-zinc-600 hover:border-zinc-400 transition-colors disabled:opacity-50"
          >
            <Send size={12} /> {sendingPreview ? t("common.sending") : t("dashboard.digestPreview")}
          </button>
          <button
            data-testid="digest-toggle"
            onClick={() => toggleDigest(!digestEnabled)}
            role="switch"
            aria-checked={digestEnabled}
            className={`w-11 h-6 rounded-full relative transition-colors shrink-0 ${digestEnabled ? "bg-[#FF4F00]" : "bg-zinc-300"}`}
          >
            <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${digestEnabled ? "translate-x-5" : "translate-x-0.5"}`} />
          </button>
        </div>
      </main>
    </div>
  );
}
